import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DespachoInfoCallout, DespachoSectionCard, DespachoSimpleTable } from "@/components/despacho/DespachoUi";
import { despachoFiscalInfoApi, parseFiscalInfoError } from "@/modules/despacho/despachoFiscalInfoApi";

function flattenBoardClients(groups) {
  const out = [];
  for (const g of groups || []) {
    for (const row of g.items || []) {
      if (row?.fiscal_client_id) {
        out.push({
          id: row.fiscal_client_id,
          name: row.nombre || row.razon_social || row.client_name || row.fiscal_client_id,
        });
      }
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name, "es"));
}

const STATUS_LABELS = {
  pending: "Pendiente envío",
  borrador: "Pendiente envío",
  generated: "Pendiente envío",
  sent_manually: "Enviado",
  skipped_not_needed: "No necesario",
  skipped_already_sent: "Ya enviado",
  copied: "Copiado",
};

export default function ClientReminderTasksPanel({ readOnly = false }) {
  const [drafts, setDrafts] = useState([]);
  const [clients, setClients] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadState, setLoadState] = useState("loading");
  const [formOpen, setFormOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    fiscal_client_id: "",
    template_id: "",
    subject: "",
    body: "",
    scheduled_for: "",
  });

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const [rows, board, tpls] = await Promise.all([
        despachoFiscalInfoApi.listClientMessageDrafts(),
        despachoFiscalInfoApi.getBoard(),
        despachoFiscalInfoApi.listMessageTemplates(),
      ]);
      setDrafts(rows);
      setClients(flattenBoardClients(board.groups));
      setTemplates(tpls);
      setLoadState("ready");
    } catch {
      setDrafts([]);
      setLoadState("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patchStatus = async (draftId, status) => {
    if (readOnly) return;
    try {
      await despachoFiscalInfoApi.patchClientMessageDraft(draftId, { status });
      toast.success("Estado actualizado.");
      await load();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    }
  };

  const applyTemplate = (templateId) => {
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return;
    setForm((f) => ({
      ...f,
      template_id: templateId,
      subject: tpl.template_name || "",
      body: tpl.body || "",
    }));
  };

  const createReminder = async () => {
    if (readOnly || !form.fiscal_client_id || !form.body.trim()) {
      toast.error("Seleccione empresa y escriba el mensaje.");
      return;
    }
    setBusy(true);
    try {
      await despachoFiscalInfoApi.createClientMessageDraft({
        fiscal_client_id: form.fiscal_client_id,
        subject: form.subject || "Recordatorio al cliente",
        body: form.body.trim(),
        scheduled_for: form.scheduled_for || undefined,
        template_id: form.template_id || undefined,
        message_type: "manual_reminder",
      });
      toast.success("Recordatorio programado.");
      setFormOpen(false);
      setForm({ fiscal_client_id: "", template_id: "", subject: "", body: "", scheduled_for: "" });
      await load();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "scheduled_for",
        label: "Programado",
        render: (r) => (r.scheduled_for ? String(r.scheduled_for).slice(0, 10) : "—"),
      },
      { key: "client_name", label: "Cliente", render: (r) => r.client_name || r.fiscal_client_id || "—" },
      { key: "subject", label: "Asunto", render: (r) => r.subject || r.message_type || "—" },
      {
        key: "status",
        label: "Estado",
        render: (r) => STATUS_LABELS[String(r.status || "").toLowerCase()] || r.status || "—",
      },
      {
        key: "actions",
        label: "Acción auxiliar",
        render: (r) => {
          const st = String(r.status || "").toLowerCase();
          if (["sent_manually", "skipped_not_needed", "skipped_already_sent"].includes(st)) {
            return <span className="text-xs text-slate-500">Cerrado</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {!readOnly ? (
                <>
                  <Button type="button" size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={() => patchStatus(r.id, "sent_manually")}>
                    Enviado
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="rounded-sm h-7 text-[11px]" onClick={() => patchStatus(r.id, "skipped_already_sent")}>
                    Ya lo mandé
                  </Button>
                  <Button type="button" size="sm" variant="ghost" className="rounded-sm h-7 text-[11px]" onClick={() => patchStatus(r.id, "skipped_not_needed")}>
                    No necesario
                  </Button>
                </>
              ) : null}
            </div>
          );
        },
      },
    ],
    [readOnly],
  );

  const groups = useMemo(() => {
    const byClient = new Map();
    for (const d of drafts) {
      const key = d.fiscal_client_id || "sin_cliente";
      if (!byClient.has(key)) byClient.set(key, []);
      byClient.get(key).push(d);
    }
    return [...byClient.entries()].map(([clientId, rows]) => {
      const client = clients.find((c) => c.id === clientId);
      return {
        clientId,
        clientName: client?.name || clientId,
        rows: rows.map((r) => ({
          ...r,
          client_name: client?.name || r.fiscal_client_id,
        })),
      };
    });
  }, [drafts, clients]);

  if (loadState === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando recordatorios…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-reminder-tasks-panel">
      <DespachoInfoCallout>
        Recordatorios a clientes <strong>por empresa</strong>. El auxiliar decide si envía, omite o marca como no necesario.
        Las plantillas maestras se definen en la pestaña <strong>Plantillas programadas</strong> de este mismo módulo.
        El historial de seguimiento fiscal marcado en la obligación está en <strong>Seguimiento enviado</strong>.
      </DespachoInfoCallout>

      {!readOnly ? (
        <Button type="button" size="sm" className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]" onClick={() => setFormOpen(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />
          Agregar recordatorio por empresa
        </Button>
      ) : null}

      {groups.length ? (
        groups.map((g) => (
          <DespachoSectionCard key={g.clientId} title={g.clientName} description={`${g.rows.length} recordatorio(s)`}>
            <DespachoSimpleTable columns={columns} rows={g.rows} emptyLabel="Sin recordatorios." />
          </DespachoSectionCard>
        ))
      ) : (
        <DespachoSectionCard title="Recordatorios a clientes">
          <p className="text-sm text-slate-600 m-0">No hay recordatorios programados. Agregue uno por empresa.</p>
        </DespachoSectionCard>
      )}

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="rounded-sm sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Recordatorio al cliente</DialogTitle>
            <DialogDescription>Programación por empresa. El auxiliar gestionará el envío desde esta lista.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Empresa</Label>
              <Select
                value={form.fiscal_client_id || undefined}
                onValueChange={(v) => setForm((f) => ({ ...f, fiscal_client_id: v }))}
              >
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Seleccione…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Plantilla (opcional)</Label>
              <Select
                value={form.template_id || "__none__"}
                onValueChange={(v) => {
                  if (v === "__none__") {
                    setForm((f) => ({ ...f, template_id: "" }));
                  } else {
                    applyTemplate(v);
                  }
                }}
              >
                <SelectTrigger className="rounded-sm"><SelectValue placeholder="Texto libre…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Texto libre</SelectItem>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.template_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fecha programación</Label>
              <Input type="date" className="rounded-sm h-9" value={form.scheduled_for} onChange={(e) => setForm((f) => ({ ...f, scheduled_for: e.target.value }))} />
            </div>
            <SpellcheckTextarea
              label="Mensaje"
              className="rounded-sm text-sm min-h-[100px]"
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-sm" onClick={() => setFormOpen(false)}>Cancelar</Button>
            <Button type="button" className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]" disabled={busy} onClick={createReminder}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
